import React, { useEffect, useState, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

const IMG = process.env.REACT_APP_ATTACHMENT_URL;

const dims = (a) => (a && a.width && a.height ? `${a.width}×${a.height}` : null);

// Review queue for near-duplicate images surfaced by the nightly
// find-attachment-near-dupes job. Pick the one to keep → Merge (drops the
// others' links on that libation), or Not duplicates → dismiss (the finder
// remembers and won't re-surface the same images together).
export default function DuplicateImages() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [keep, setKeep] = useState({}); // groupId -> chosen attachmentId
  const [viewer, setViewer] = useState(null); // { groupId, index } | null

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await client.service('admin/attachment-duplicate-group').find({
        query: { status: 'pending', $limit: 50, $sort: { maxDistance: 1 } },
      });
      setGroups(res.data || []);
      // default keeper = representativeId (or first attachment)
      const k = {};
      (res.data || []).forEach((g) => {
        k[g.id] = g.representativeId || (g.attachmentIds || [])[0];
      });
      setKeep(k);
    } catch (e) {
      setError('Failed to load duplicate groups.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Viewer (lightbox) state helpers
  const vGroup = viewer && (groups || []).find((g) => g.id === viewer.groupId);
  const vList = (vGroup && vGroup.attachments) || [];
  const vAtt = vList[viewer ? viewer.index : 0];
  const closeViewer = useCallback(() => setViewer(null), []);
  const stepViewer = useCallback((delta) => {
    setViewer((v) => {
      if (!v) return v;
      const g = (groups || []).find((gg) => gg.id === v.groupId);
      const n = ((g && g.attachments) || []).length;
      if (!n) return v;
      return { ...v, index: (v.index + delta + n) % n };
    });
  }, [groups]);

  // Keyboard nav while the viewer is open
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowRight') stepViewer(1);
      else if (e.key === 'ArrowLeft') stepViewer(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer, closeViewer, stepViewer]);

  const resolve = async (group, patch) => {
    setBusyId(group.id);
    try {
      await client.service('admin/attachment-duplicate-group').patch(group.id, patch);
      setGroups((cur) => (cur || []).filter((g) => g.id !== group.id));
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const merge = (g) => resolve(g, { status: 'resolved', keepId: keep[g.id] });
  const dismiss = (g) => resolve(g, { status: 'dismissed' });

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200">
        <Breadcrumb elements={[
          { name: 'Reports', href: '/admin/reports' },
          { name: 'Duplicate Images' },
        ]}
        />
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-1">Duplicate Images</h1>
        <p className="text-sm text-gray-500 max-w-3xl">
          Near-duplicate wine images found by perceptual hashing (catches the same
          image re-encoded/resized across sources, which exact-match dedup misses).
          <strong> Click any image to enlarge</strong> and use ←/→ (or the arrows) to
          flip between them at the same size for comparison. Pick the one to{' '}
          <strong>keep</strong> and merge, or mark <strong>not duplicates</strong> —
          dismissed pairs won&apos;t be surfaced again.
        </p>
      </div>

      {error && <div className="text-sm text-red-700 mb-4">{error}</div>}
      {!error && groups === null && <div className="text-sm text-gray-500">Loading…</div>}
      {!error && groups && groups.length === 0 && (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500 italic">
          No duplicate groups to review. The nightly finder will add new ones here.
        </div>
      )}

      {!error && groups && groups.length > 0 && (
        <div className="space-y-4">
          {groups.map((g) => {
            // highest-resolution attachment in the group (matches the finder's keeper pick)
            const best = (g.attachments || []).reduce((b, a) => {
              const pa = a.width && a.height ? a.width * a.height : -1;
              const pb = b && b.width && b.height ? b.width * b.height : -1;
              return pa > pb ? a : (b || a);
            }, null);
            return (
              <div key={g.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center gap-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900">
                      {g.listingId ? (
                        <NavLink to={`/admin/listings/${g.listingId}`} className="text-indigo-700 hover:underline">
                          {g.libationName || 'wine'}
                        </NavLink>
                      ) : (g.libationName || 'wine')}
                    </h3>
                    <div className="mt-1 text-xs text-gray-500">
                      {(g.attachments || []).length} similar images · distance {g.maxDistance ?? '—'}/64
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => dismiss(g)}
                      disabled={busyId === g.id}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      title="These are different images — never group them again."
                    >
                      Not duplicates
                    </button>
                    <button
                      type="button"
                      onClick={() => merge(g)}
                      disabled={busyId === g.id || !keep[g.id]}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                    >
                      {busyId === g.id ? 'Working…' : 'Merge — keep selected'}
                    </button>
                  </div>
                </div>

                <div className="px-4 py-3 flex flex-wrap gap-4">
                  {(g.attachments || []).map((a, i) => {
                    const selected = keep[g.id] === a.id;
                    const isBest = best && a.id === best.id;
                    return (
                      <div
                        key={a.id}
                        className={`rounded-lg border-2 p-1 ${selected ? 'border-primary' : 'border-transparent'}`}
                      >
                        <button
                          type="button"
                          onClick={() => setViewer({ groupId: g.id, index: i })}
                          className="block relative group"
                          title="Click to enlarge / compare"
                        >
                          <img
                            src={`${IMG}/${a.path}?w=180&h=180&f=inside`}
                            alt=""
                            className="w-40 h-40 object-contain bg-gray-50 rounded"
                          />
                          <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 text-white text-xs font-medium rounded transition-opacity">
                            Click to enlarge
                          </span>
                          {isBest && (
                            <span className="absolute top-1 left-1 bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                              highest-res
                            </span>
                          )}
                        </button>
                        <label className="mt-1 flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="radio"
                            name={`keep-${g.id}`}
                            checked={selected}
                            onChange={() => setKeep((k) => ({ ...k, [g.id]: a.id }))}
                          />
                          {selected ? 'Keep this' : 'Keep'}
                          <span className="ml-auto text-gray-400">{dims(a) || '—'}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox / compare viewer */}
      {viewer && vAtt && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex flex-col"
          onClick={closeViewer}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm">
              <span className="font-semibold">{vGroup.libationName || 'wine'}</span>
              <span className="text-white/60"> · {viewer.index + 1} / {vList.length}</span>
              <span className="text-white/60"> · {dims(vAtt) || 'dimensions unknown'}</span>
              {vGroup.representativeId === vAtt.id && (
                <span className="ml-2 bg-green-600 text-[11px] px-1.5 py-0.5 rounded">recommended (highest-res)</span>
              )}
            </div>
            <button type="button" onClick={closeViewer} className="text-white/80 hover:text-white text-2xl leading-none px-2" title="Close (Esc)">×</button>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 min-h-0" onClick={(e) => e.stopPropagation()}>
            {vList.length > 1 && (
              <button type="button" onClick={() => stepViewer(-1)} className="text-white/70 hover:text-white text-4xl px-3 shrink-0" title="Previous (←)">‹</button>
            )}
            <img
              src={`${IMG}/${vAtt.path}?w=1600&f=inside`}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
            {vList.length > 1 && (
              <button type="button" onClick={() => stepViewer(1)} className="text-white/70 hover:text-white text-4xl px-3 shrink-0" title="Next (→)">›</button>
            )}
          </div>

          <div className="px-4 py-4 flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setKeep((k) => ({ ...k, [viewer.groupId]: vAtt.id }))}
              className={`px-4 py-2 rounded-md text-sm font-medium ${keep[viewer.groupId] === vAtt.id ? 'bg-green-600 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
            >
              {keep[viewer.groupId] === vAtt.id ? '✓ Keeping this' : 'Keep this one'}
            </button>
          </div>
        </div>
      )}
    </Page>
  );
}
